import { useRef, useState, useEffect, useCallback } from 'react';

interface SectionHeights {
  manageHeight: number;
  growHeight: number;
  businessHeight: number;
  totalHeight: number;
  managePercent: number;
  growPercent: number;
  businessPercent: number;
}

/**
 * Hook to measure actual heights of MANAGE, GROW, and BUSINESS sections
 * Returns percentage-based heights for background positioning
 */
export function useSectionHeights() {
  const containerRef = useRef<HTMLDivElement>(null);
  const manageStartRef = useRef<HTMLDivElement>(null);
  const growStartRef = useRef<HTMLDivElement>(null);
  const businessStartRef = useRef<HTMLDivElement>(null);
  const businessEndRef = useRef<HTMLDivElement>(null);

  // Debounce refs for resize handling (must be at top level, not inside useEffect)
  const resizeDebounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastHeightsRef = useRef<SectionHeights | null>(null);

  const [heights, setHeights] = useState<SectionHeights>({
    manageHeight: 0,
    growHeight: 0,
    businessHeight: 0,
    totalHeight: 0,
    managePercent: 33.33,
    growPercent: 33.33,
    businessPercent: 33.34,
  });

  const calculateHeights = useCallback(() => {
    if (!containerRef.current) return;

    const container = containerRef.current;
    const containerRect = container.getBoundingClientRect();
    const containerTop = containerRect.top;

    // Get positions relative to container
    const manageTop = manageStartRef.current
      ? manageStartRef.current.getBoundingClientRect().top - containerTop
      : 0;

    const growTop = growStartRef.current
      ? growStartRef.current.getBoundingClientRect().top - containerTop
      : containerRect.height * 0.33;

    const businessTop = businessStartRef.current
      ? businessStartRef.current.getBoundingClientRect().top - containerTop
      : containerRect.height * 0.66;

    const businessEnd = businessEndRef.current
      ? businessEndRef.current.getBoundingClientRect().bottom - containerTop
      : containerRect.height;

    // Calculate section heights
    const manageHeight = growTop - manageTop;
    const growHeight = businessTop - growTop;
    const businessHeight = businessEnd - businessTop;
    const totalHeight = businessEnd - manageTop;

    // Calculate percentages
    const managePercent = totalHeight > 0 ? (manageHeight / totalHeight) * 100 : 33.33;
    const growPercent = totalHeight > 0 ? (growHeight / totalHeight) * 100 : 33.33;
    const businessPercent = totalHeight > 0 ? (businessHeight / totalHeight) * 100 : 33.34;

    setHeights({
      manageHeight,
      growHeight,
      businessHeight,
      totalHeight,
      managePercent,
      growPercent,
      businessPercent,
    });
  }, []);

  useEffect(() => {
    // Initial calculation after render
    const timeoutId = setTimeout(calculateHeights, 100);

    // Create ResizeObserver to recalculate on resize
    const resizeObserver = new ResizeObserver(() => {
      // Clear existing debounce timeout
      if (resizeDebounceTimerRef.current) {
        clearTimeout(resizeDebounceTimerRef.current);
      }

      // Debounce the calculation
      resizeDebounceTimerRef.current = setTimeout(() => {
        calculateHeights();
      }, 100);
    });

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    // Also recalculate on window resize (already debounced by browser)
    window.addEventListener('resize', calculateHeights);

    return () => {
      clearTimeout(timeoutId);
      if (resizeDebounceTimerRef.current) {
        clearTimeout(resizeDebounceTimerRef.current);
      }
      resizeObserver.disconnect();
      window.removeEventListener('resize', calculateHeights);
    };
  }, [calculateHeights]);

  return {
    containerRef,
    manageStartRef,
    growStartRef,
    businessStartRef,
    businessEndRef,
    ...heights,
  };
}
